import { useCallback, useLayoutEffect, useRef } from "react"

export type UseAutosizeTextareaOptions = {
  /** Maximum visible rows before vertical scroll appears. Default: 15 */
  maxRows?: number
  /** Minimum visible rows when empty or short content. Default: 1 */
  minRows?: number
  /** Pixel floor; combined with minRows via max(). */
  minHeightPx?: number
  /** Pixel cap; combined with maxRows via min(). */
  maxHeightPx?: number
}

function verticalBoxExtras(styles: CSSStyleDeclaration): number {
  return (
    Number.parseFloat(styles.paddingTop) +
    Number.parseFloat(styles.paddingBottom) +
    Number.parseFloat(styles.borderTopWidth) +
    Number.parseFloat(styles.borderBottomWidth)
  )
}

function rowHeightPx(textarea: HTMLTextAreaElement, styles: CSSStyleDeclaration, extras: number): number {
  const parsedLineHeight = Number.parseFloat(styles.lineHeight)
  if (Number.isFinite(parsedLineHeight)) return parsedLineHeight

  const savedValue = textarea.value
  textarea.value = "A"
  const singleLineHeight = textarea.scrollHeight - extras
  textarea.value = savedValue
  return Math.max(singleLineHeight, 1)
}

function resizeTextarea(
  textarea: HTMLTextAreaElement,
  maxRows: number,
  minRows: number,
  minHeightPx?: number,
  maxHeightPx?: number,
) {
  textarea.style.height = "0px"

  const styles = window.getComputedStyle(textarea)
  const extras = verticalBoxExtras(styles)
  const rowHeight = rowHeightPx(textarea, styles, extras)
  const minHeight = Math.max(rowHeight * minRows + extras, minHeightPx ?? 0)
  let maxHeight = rowHeight * maxRows + extras
  if (maxHeightPx != null && Number.isFinite(maxHeightPx)) {
    maxHeight = Math.min(maxHeight, Math.max(0, maxHeightPx))
  }
  if (maxHeight < minHeight) maxHeight = minHeight

  const contentHeight = textarea.scrollHeight
  const nextHeight = Math.min(Math.max(contentHeight, minHeight), maxHeight)

  textarea.style.height = `${nextHeight}px`
  textarea.style.overflowY = contentHeight > maxHeight ? "auto" : "hidden"
}

/**
 * Keeps a textarea height in sync with its content, capped at `maxRows`.
 * Pair with {@link AutosizeTextarea} or wire `ref` + `resize()` into a custom textarea.
 */
export function useAutosizeTextarea(value: string, options: UseAutosizeTextareaOptions = {}) {
  const { maxRows = 15, minRows = 1, minHeightPx, maxHeightPx } = options
  const ref = useRef<HTMLTextAreaElement>(null)

  const resize = useCallback(() => {
    const textarea = ref.current
    if (!textarea) return
    resizeTextarea(textarea, maxRows, minRows, minHeightPx, maxHeightPx)
  }, [maxHeightPx, maxRows, minHeightPx, minRows])

  useLayoutEffect(() => {
    resize()
  }, [value, resize])

  useLayoutEffect(() => {
    const textarea = ref.current
    if (!textarea || typeof ResizeObserver === "undefined") return

    const observer = new ResizeObserver(() => resize())
    observer.observe(textarea)
    return () => observer.disconnect()
  }, [resize])

  return { ref, resize }
}
